import React, { useState, useRef, useEffect, useCallback } from "react";

import { minutesAndSeconds } from "../../Utils/timeFormat";

function SeekBar({ track, currentPosition, onSeek }) {
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef(null);
  const justFinishedDragging = useRef(false);

  const trackLength = track ? track.duration : 0;
  const elapsed = minutesAndSeconds(parseInt(currentPosition));
  const remaining = minutesAndSeconds(trackLength - parseInt(currentPosition));

  const progressPercentage = trackLength > 0
    ? (currentPosition / trackLength) * 100
    : 0;

  const calculateSeekPosition = useCallback((clientX) => {
    if (!trackRef.current || !onSeek) return;

    const rect = trackRef.current.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newPosition = percentage * trackLength;

    onSeek(Math.max(0, Math.min(newPosition, trackLength)));
  }, [trackLength, onSeek]);

  const handleSeek = (e) => {
    // Prevent click from firing right after drag ends
    if (justFinishedDragging.current) {
      justFinishedDragging.current = false;
      return;
    }
    calculateSeekPosition(e.clientX);
  };

  const handleMouseDown = (e) => {
    // Prevent text selection during drag
    e.preventDefault();
    setIsDragging(true);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    calculateSeekPosition(e.touches[0].clientX);
  };

  // Global mouse/touch event handlers for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      calculateSeekPosition(e.clientX);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      calculateSeekPosition(e.touches[0].clientX);
    };

    const handleEnd = () => {
      setIsDragging(false);
      justFinishedDragging.current = true;
      // Reset the flag after a brief delay
      setTimeout(() => {
        justFinishedDragging.current = false;
      }, 10);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, calculateSeekPosition]);

  return (
    <div className="flex items-center flex-1 gap-2">
      <span className="text-[10px] text-text-muted font-mono tabular-nums shrink-0">
        {`${elapsed[0]}:${elapsed[1]}`}
      </span>

      {/* Seek Bar Track - Wrapper for easier clicking */}
      <div
        ref={trackRef}
        className={`relative w-full py-1.5 ${onSeek ? 'cursor-pointer' : ''} ${isDragging ? 'select-none' : ''}`}
        style={{ touchAction: 'none' }}
        onClick={handleSeek}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Actual Track Bar */}
        <div
          className="relative w-full h-1 bg-neutral-800 rounded-full overflow-visible group border border-neutral-900"
        >
          {/* Progress Bar */}
          <div
            className="absolute top-0 left-0 h-full bg-accent rounded-full transition-all duration-100"
            style={{ width: `${progressPercentage}%`, boxShadow: '0 0 8px rgba(59,130,246,0.55)' }}
          />

          {/* Seek Handle/Thumb */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md transition-all duration-100 ${
              isDragging ? 'scale-125' : 'scale-100'
            } ${onSeek ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}
            style={{ left: `${progressPercentage}%`, transform: 'translate(-50%, -50%)' }}
          />
        </div>
      </div>

      <span className="text-[10px] text-text-dim font-mono tabular-nums shrink-0">
        {trackLength > 1 ? `-${remaining[0]}:${remaining[1]}` : ''}
      </span>
    </div>
  );
}

export default SeekBar;
